import { observer } from "mobx-react";
import { useStores } from "stores";
import { Flex, Box, Text, Link } from "@chakra-ui/react";
import type { FieldProps } from "formik";
import { Formik, Form, Field } from "formik";
import { useMutation } from "@apollo/client";
import { SEND_NEW_PASSWORD } from "api/mutations";
import { Button } from "components/button/Button";
import { InputGroup } from "components/input/InputGroup";
import * as Yup from "yup";
import { Filler } from "components/filler/Filler";
import ClickIllusttration from "assets/illustrations/neutral/Click.svg?react";
import { Container } from "components/container/Container";
import { signUpComponentStyles } from "./registerComponents";

const emailValidationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Required"),
});

export const ForgotPasswordLink = ({ ...customStyles }) => {
  const { authStore } = useStores();
  const handleClick = () => {
    authStore.setForgotPassword(!authStore.forgotPassword);
  };
  return (
    <Link
      data-cy="forgot-password"
      onClick={handleClick}
      display="block"
      fontSize="sm"
      {...customStyles}
    >
      Forgot password?
    </Link>
  );
};

export const ForgotPassword = observer(function ForgotPassword() {
  const { authStore } = useStores();
  const { isResetPasswordPending, forgotPassword } = authStore;
  const [sendPasswordReset, { data, reset }] = useMutation(SEND_NEW_PASSWORD);

  const handleClick = () => {
    authStore.setResetPasswordStatus(false);
    reset();
  };
  const handlePasswordFormClick = () =>
    authStore.setForgotPassword(!forgotPassword);

  if (isResetPasswordPending) {
    return (
      <ForgotPasswordMessage
        onClickResend={handleClick}
        onClickSignIn={handlePasswordFormClick}
      />
    );
  }
  if (data) {
    authStore.setResetPasswordStatus(true);
  }

  return (
    <Formik
      initialValues={{ email: "" }}
      validationSchema={emailValidationSchema}
      onSubmit={async ({ email }: any, { setSubmitting }) => {
        setSubmitting(true);
        await sendPasswordReset({
          variables: {
            email,
          },
        });
        setSubmitting(false);
        return;
      }}
    >
      {({ isSubmitting, values }) => (
        <PasswordForm
          isSubmitting={isSubmitting}
          values={values}
          onClickSignIn={handlePasswordFormClick}
        />
      )}
    </Formik>
  );
});

export const PasswordForm = ({ isSubmitting, onClickSignIn }: any) => (
  <Form noValidate>
    <Container {...signUpComponentStyles}>
      <Flex
        gap="4"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <Filler.SVGFiller
          illustration={<ClickIllusttration height="100%" width="100%" />}
          variant="other"
          headline="Forgot your password?"
          mt="0"
        />
        <Box textAlign="center" mb="10" textStyle="base">
          Dangit. We hate when that happens. Please enter the email address you
          used to register.
        </Box>
      </Flex>
      <Field name="email">
        {({ field, meta }: FieldProps) => (
          <InputGroup
            data-cy="forgot-email"
            label="Email address"
            placeholder="Email address"
            error={meta.touched && meta.error ? meta.error : null}
            layoutProps={{ mb: "10" }}
            {...field}
          />
        )}
      </Field>
      <Button
        data-cy="forgot-button"
        type="submit"
        size="lg"
        maxW="unset"
        w="100%"
        mb="6"
        isLoading={isSubmitting}
      >
        Reset my password
      </Button>
      <Flex gap="0.5" textStyle="caption" justifyContent="center">
        Remembered your password?
        <Link
          data-cy="remembered-password"
          onClick={onClickSignIn}
          cursor="pointer"
          textStyle="captionLink"
        >
          Log in
        </Link>
      </Flex>
    </Container>
  </Form>
);

export const ForgotPasswordMessage = ({
  onClickResend,
  onClickSignIn,
}: any) => (
  <Container>
    <Flex
      gap="4"
      flexDirection="column"
      alignItems="center"
      textAlign="center"
      {...signUpComponentStyles}
    >
      <Filler.SVGFiller
        imagePath="neutral/Click"
        variant="other"
        headline="Please check your email"
        mt="0"
      />
      <Box mb="6" textStyle="base">
        We just sent a link to reset your password. <br />
        Please check your email.
      </Box>
      <Button
        type="submit"
        size="lg"
        maxW="unset"
        w="100%"
        mb="2"
        onClick={onClickSignIn}
      >
        Log in
      </Button>
      <Flex>
        <Text as="span" textStyle="caption">
          Did not receive email?&nbsp;
        </Text>
        <Link textStyle="captionLink" onClick={onClickResend}>
          Resend
        </Link>
      </Flex>
    </Flex>
  </Container>
);
